import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// CN function for combining and merging Tailwind CSS classes
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const withOptionalChaining = (fn) => {
  try {
    return fn?.() || null;
  } catch {
    return null;
  }
};

const catchAsyncErrors = (asyncFn) => {
  return async (...args) => {
    try {
      return await asyncFn?.(...args);
    } catch (error) {
      console.error('Async operation failed:', error);
      return null;
    }
  };
};

// Current Agent Service Error Detection and Auto-Repair
const currentAgentServiceErrorRepair = {
  async diagnose() {
    console.log('🔍 DIAGNOSTIC: Analyzing current agent service issues...');
    
    try {
      // Try to import and test the service
      const { currentAgentService } = await import('../services/currentAgentService');
      
      // Test basic functionality
      const testResult = await currentAgentService?.getCurrentAgent();
      console.log('✅ DIAGNOSTIC: CurrentAgentService is accessible:', !!testResult);
      
      return {
        serviceAccessible: true,
        testData: testResult,
        timestamp: new Date()?.toISOString()
      };
      
    } catch (error) {
      console.error('❌ DIAGNOSTIC: CurrentAgentService failed:', error);
      
      return {
        serviceAccessible: false,
        error: error?.message,
        errorType: error?.constructor?.name,
        timestamp: new Date()?.toISOString()
      };
    }
  },

  async autoRepair() {
    console.log('🔧 AUTO-REPAIR: Attempting to fix current agent service...');
    
    // Clear all possible caches
    try {
      sessionStorage?.clear();
      localStorage?.clear();
      
      // Force reload of the module
      if (typeof window !== 'undefined' && window?.location) {
        window?.location?.reload();
      }
      
      console.log('✅ AUTO-REPAIR: Cache cleared and reload initiated');
      return { repairAttempted: true, method: 'cache_clear_and_reload' };
      
    } catch (error) {
      console.error('❌ AUTO-REPAIR: Failed:', error);
      return { repairAttempted: false, error: error?.message };
    }
  }
};

// Global error boundary for agent service
const globalAgentServiceErrorHandler = {
  init() {
    if (typeof window !== 'undefined') {
      // Catch unhandled promise rejections
      window?.addEventListener('unhandledrejection', (event) => {
        if (event?.reason?.message?.includes('currentAgent') || 
            event?.reason?.message?.includes('bail_agent')) {
          console.log('🚨 GLOBAL: Agent service error detected, initiating auto-repair...');
          currentAgentServiceErrorRepair?.autoRepair();
        }
      });
      
      // Catch general errors
      window?.addEventListener('error', (event) => {
        if (event?.message?.includes('currentAgent') || 
            event?.message?.includes('Load failed')) {
          console.log('🚨 GLOBAL: Load failure detected, initiating diagnostics...');
          currentAgentServiceErrorRepair?.diagnose()?.then(result => {
            console.log('📊 DIAGNOSTIC RESULT:', result);
            if (!result?.serviceAccessible) {
              currentAgentServiceErrorRepair?.autoRepair();
            }
          });
        }
      });
      
      console.log('✅ GLOBAL: Agent service error handler initialized');
    }
  }
};

// Initialize error handling
globalAgentServiceErrorHandler?.init();

// Enhanced utility functions with error handling
const utils = {
  // Safe string operations
  truncateText: (text, maxLength = 100) => {
    return withOptionalChaining(() => {
      if (!text || typeof text !== 'string') return '';
      return text?.length > maxLength 
        ? text?.substring(0, maxLength) + '...' 
        : text;
    });
  },

  // Safe array operations
  safeArrayMap: (array, mapFn) => {
    return withOptionalChaining(() => {
      if (!Array?.isArray(array)) return [];
      return array?.map(mapFn) || [];
    });
  },

  // Safe date formatting
  formatDate: (date, options = {}) => {
    return withOptionalChaining(() => {
      if (!date) return 'N/A';
      const dateObj = new Date(date);
      return dateObj?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options
      });
    });
  },

  // Safe phone formatting
  formatPhoneNumber: (phone) => {
    return withOptionalChaining(() => {
      if (!phone) return '';
      const cleaned = phone?.replace(/\D/g, '');
      if (cleaned?.length === 10) {
        return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6)}`;
      }
      return phone;
    });
  },

  // Safe currency formatting
  formatCurrency: (amount) => {
    return withOptionalChaining(() => {
      if (amount === null || amount === undefined) return '$0.00';
      const num = Number(amount);
      if (isNaN(num)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      })?.format(num);
    });
  },

  // Safe object path access
  getNestedValue: (obj, path, defaultValue = null) => {
    return withOptionalChaining(() => {
      return path?.split('.')?.reduce((current, key) => {
        return current?.[key];
      }, obj) ?? defaultValue;
    });
  },

  // Safe async operations wrapper
  safeAsync: catchAsyncErrors,

  // Error diagnosis helper
  diagnoseCurrentAgentService: currentAgentServiceErrorRepair?.diagnose,
  
  // Auto repair helper
  repairCurrentAgentService: currentAgentServiceErrorRepair?.autoRepair
};

export default utils;